/**
 * Phase 5 PR 5.1 — `useTodaySession` hook.
 *
 * Composes today's workout from the RESOLVED current-week training_plan
 * (Season-model 2b — the macro `resolveCurrentWeek` week, passed in as
 * `weekNumber`; NO `MAX(week_number)` fallback) plus the plan_day matching
 * today's Monday-based day_of_week (in local TZ). This keeps the hero's
 * session, the footer "Week X of Y", and the day-rows all on the SAME week.
 *
 * Returns the existing `TodaySession` shape consumed by the route's
 * `renderTodayHero` adapter (Phase 6c PR 4 — feeds `<TodayHero />`).
 *
 * Returns `null` when:
 *   - no active season → caller renders `<TodayHero variant="firstRun" />`
 *   - active season but no training_plans → caller renders empty-state
 *   - active season + plans, but no plan_day for today's weekday → caller
 *     renders empty-state (incomplete plan)
 *
 * Per Phase 5 plan §"Decision 4": no fixture fallback at runtime. The
 * dashboard NEVER flashes another athlete's workout content during
 * loading.
 */

import { useCallback, useEffect, useState } from 'react';
import { useAuthContext } from '../contexts/AuthContext';
import type { TodaySession } from '../fixtures/plan';
import { todayMondayBasedLocal } from '../lib/weekdayUtils';

export interface UseTodaySessionArgs {
  /** Active season id (typically from `useActiveSeason`). When null,
   *  the hook returns null without querying. */
  seasonId: string | null;
  /**
   * Season-model 2b — the macro plan's resolved current `week_number`
   * (from `resolveCurrentWeek` via `phaseFromMacroPlan`). The hook composes
   * today's session from THAT week's training_plan. When null/undefined
   * (before_season/no_plan/invalid) it returns null — there is NO
   * `MAX(week_number)` fallback, so the hero never shows a stale week's
   * workout while the footer/day-rows show a different week.
   */
  weekNumber?: number | null;
}

export interface UseTodaySessionResult {
  data: TodaySession | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

interface PlanDayRow {
  id: string;
  day_of_week: number;
  day_label: string;
  workout_type: string;
  details: string | null;
  expanded_details: Record<string, string> | null;
}

interface TrainingPlanRow {
  id: string;
  week_number: number;
  title: string;
  intensity: string | null;
  plan_days: PlanDayRow[] | null;
}

const HERO_COACH_NAME = 'Training Coach';
const SUBTITLE_MAX = 140;

/**
 * Pull a leading distance figure out of a free-text details string.
 * Returns the formatted phrase (e.g. "4 miles", or "10.2 km" when the text states
 * kilometres and no miles) or null.
 *
 * B338 — miles win whenever the text names them, exactly as before, so a mixed
 * "10 km (6.2 mi)" and every miles day read the same. Text that states only
 * kilometres used to get no distance in the hero title at all. A pace ("5:30/km")
 * never matches: the "/" breaks the digit-to-unit adjacency, and a speed ("10 km/h")
 * is no distance either.
 *
 * B338 fix 1 (critic M2) — the phrase is the day's distance or NONE, never a rep or
 * one leg of several. It is null when a rep multiplier comes before the distance
 * ("5 x 1 km at 3:40/km", "6 x 1000m at 3:30/km, 2 km cooldown": the reps phrase, or
 * nothing, reads instead) or when the text states more than one distance. A figure
 * restated in the other unit in brackets ("10 km (6.2 mi)") is the same distance.
 * A distance that leads a rep set ("8 miles with 5x400m strides") is still the day.
 * A figure the text itself calls the total ("8 mi total: 2 mi warm-up, ...") wins, as
 * it does in `components/chat/PlanTable.tsx` `extractMileage`.
 *
 * B338 fix 2 (critic M1, L-b) — a distance restated in the other unit, in brackets or
 * after a slash ("7 km (4.3 mi)", "6 mi / 9.7 km"), still gets the miles phrase here
 * (the hook does not know the athlete's unit), but `heroDistance` also returns the
 * figure the text wrote for EACH unit so `sessionInUnit` can read the athlete's own,
 * never the other unit's rounding converted ("6.9 km" for a 7 km day). A rep set
 * anywhere in the text means a warm-up or cool-down leg is not the day's distance:
 * "2 km warm-up, 6x1000m" has none, and the reps phrase reads instead.
 */
const HERO_DISTANCE_RE =
  /(?:(\d+)\s*[×xX]\s*)?(\d+(?:\.\d+)?)\s*(miles?|mi|kilometers?|kilometres?|km|meters?|metres?|m)(?![a-z]|\/(?!\s*\d+(?:\.\d+)?\s*(?:miles?|mi|kilometers?|kilometres?|km)(?![a-z])))/gi;
const HERO_TOTAL_RE =
  /(\d+(?:\.\d+)?)\s*(miles?|mi|kilometers?|kilometres?|km)\s+total\b|\btotal\s+(?:of\s+)?(\d+(?:\.\d+)?)\s*(miles?|mi|kilometers?|kilometres?|km)(?![a-z/])/i;
// A leg label right before the distance ("Warm-up 2 km", "cool down of 1.5 mi") or right
// after it with no figure between ("2 km easy warm-up").
const HERO_LEG_BEFORE_RE = /(?:warm(?:ing)?[\s-]?up|cool(?:ing)?[\s-]?down)\W*(?:(?:of|for|with|is)\W+)?$/i;
const HERO_LEG_AFTER_RE = /^\D*?\b(?:warm(?:ing)?[\s-]?up|cool(?:ing)?[\s-]?down)\b/i;

/** The distance as the plan text wrote it in each unit (set when it wrote both). */
export interface TitleEmFigures {
  mi: string;
  km: string;
}

/** `TodaySession` plus the figures `sessionInUnit` reads; absent unless the text states both units. */
export type TodaySessionWithFigures = TodaySession & { titleEmFigures?: TitleEmFigures };

interface HeroToken {
  start: number;
  end: number;
  num: string;
  isRep: boolean;
  cls: 'mi' | 'km' | 'm';
}

/** True when the comma, semicolon, line or sentence clause around [start, end) labels it a warm-up or cool-down. */
function isLabelledLeg(text: string, start: number, end: number): boolean {
  let from = 0;
  let to = text.length;
  for (const d of text.matchAll(/[,;\n]|\.(?=\s|$)/g)) {
    const i = d.index ?? 0;
    if (i < start) from = i + 1;
    else if (i >= end) {
      to = i;
      break;
    }
  }
  return HERO_LEG_BEFORE_RE.test(text.slice(from, start)) || HERO_LEG_AFTER_RE.test(text.slice(end, to));
}

function heroDistance(
  text: string | null | undefined,
): { phrase: string; figures: TitleEmFigures | null } | null {
  if (!text) return null;
  const tokens: HeroToken[] = [...text.matchAll(HERO_DISTANCE_RE)].map((t) => {
    const unit = t[3].toLowerCase();
    return {
      start: t.index ?? 0,
      end: (t.index ?? 0) + t[0].length,
      num: t[2],
      isRep: t[1] !== undefined,
      cls: unit.startsWith('mi') ? 'mi' : unit.startsWith('k') ? 'km' : 'm',
    };
  });
  // Miles and kilometres state a day's distance; metres only ever size a rep or a stride.
  const distances = tokens.filter((t) => t.cls !== 'm');
  // A figure restated in the other unit is the same distance: bracketed ("10 km (6.2 mi)")
  // or after a slash ("6 mi / 9.7 km").
  const pairs: HeroToken[][] = [];
  const stated = distances.filter((t, i) => {
    const prev = distances[i - 1];
    const between = prev ? text.slice(prev.end, t.start) : '';
    const restated =
      prev &&
      prev.cls !== t.cls &&
      ((/^\s*\(\s*$/.test(between) && /^\s*\)/.test(text.slice(t.end))) || /^\s*\/\s*$/.test(between));
    if (restated) pairs.push([prev, t]);
    return !restated;
  });
  const figuresOf = (t: HeroToken): TitleEmFigures | null => {
    const pair = pairs.find((p) => p.includes(t));
    const mi = pair?.find((p) => p.cls === 'mi');
    const km = pair?.find((p) => p.cls === 'km');
    return mi && km ? { mi: mi.num, km: km.num } : null;
  };
  const total = HERO_TOTAL_RE.exec(text);
  if (total) {
    const unit = (total[2] ?? total[4]).toLowerCase();
    const at = total.index + total[0].length;
    const named = distances.find((t) => t.start < at && t.end > total.index);
    return {
      phrase: `${total[1] ?? total[3]} ${unit.startsWith('mi') ? 'miles' : 'km'}`,
      figures: named ? figuresOf(named) : null,
    };
  }
  if (stated.length !== 1) return null;
  if (tokens.some((t) => t.isRep && t.start <= stated[0].start)) return null;
  // A rep set anywhere: a lone distance labelled a warm-up or a cool-down is a leg, not the day.
  if (tokens.some((t) => t.isRep)) {
    const legEnd = pairs.find((p) => p[0] === stated[0])?.[1].end ?? stated[0].end;
    if (isLabelledLeg(text, stated[0].start, legEnd)) return null;
  }
  // Miles win whenever the text names them, exactly as before.
  const pick = distances.find((t) => t.cls === 'mi') ?? stated[0];
  return {
    phrase: pick.cls === 'mi' ? `${pick.num} miles` : `${pick.num} km`,
    figures: figuresOf(pick),
  };
}

function extractMileagePhrase(text: string | null | undefined): string | null {
  return heroDistance(text)?.phrase ?? null;
}

/**
 * Pull a useful workout-em phrase from `details`. Used for the
 * italicized title em on the hero. Tries (in order):
 *
 *   1. Miles — "4 mi", "12.5 miles" → "4 miles"
 *   2. Rep × meters — "5x400m", "6x800m" → "5x400m" (verbatim)
 *
 * Returns null when nothing matches — caller should drop the em
 * gracefully so the hero doesn't render an awkward placeholder
 * like "Hill workout, session." (this was the PR 5.1 polish ask).
 *
 * Pure / exported for tests.
 */
function extractWorkoutEm(text: string | null | undefined): string | null {
  const milesPhrase = extractMileagePhrase(text);
  if (milesPhrase) return milesPhrase;
  if (typeof text !== 'string') return null;
  // Match "5x400m" / "5×400m" / "6 x 800 m" — case-insensitive,
  // tolerant of spacing and the unicode multiplication sign.
  const repsMatch = text.match(/(\d+)\s*[x×]\s*(\d+)\s*m\b/i);
  if (repsMatch) return `${repsMatch[1]}x${repsMatch[2]}m`;
  return null;
}

/**
 * Workout-type → display intensity classification. Mirrors
 * `useWeeklyPlan.classifyLabel` but tuned for the hero pill.
 */
function intensityFromWorkoutType(workoutType: string | null | undefined): string {
  const wt = (workoutType ?? '').toLowerCase();
  if (!wt || wt.includes('rest') || wt.includes('off')) return 'Rest';
  if (wt.includes('long')) return 'Long';
  if (wt.includes('tempo') || wt.includes('threshold')) return 'Tempo';
  if (wt.includes('interval') || wt.includes('repeat')) return 'Tough';
  if (wt.includes('hill')) return 'Tough';
  if (wt.includes('easy') || wt.includes('recovery') || wt.includes('shake')) {
    return 'Easy';
  }
  // Fallback to the type itself, capitalized.
  const w = workoutType ?? '';
  return w.charAt(0).toUpperCase() + w.slice(1).toLowerCase();
}

/**
 * Title segments for the hero serif heading. The hero splits the
 * sentence so the em phrase can color-treat the distance:
 *
 *   {titleLead} <em>{titleEm}</em>{titleTrail}
 *
 * e.g. "Easy run, 3 miles."
 */
function buildTitle(workoutType: string, details: string | null | undefined): {
  titleLead: string;
  titleEm: string;
  titleTrail: string;
} {
  const wt = (workoutType || '').toLowerCase();
  const em = extractWorkoutEm(details);

  if (wt.includes('rest') || wt.includes('off')) {
    return { titleLead: '', titleEm: 'Rest day', titleTrail: '.' };
  }

  let leadWithComma: string;
  let leadPlain: string;
  if (wt.includes('long')) {
    leadWithComma = 'Long run,';
    leadPlain = 'Long run';
  } else if (wt.includes('tempo')) {
    leadWithComma = 'Tempo run,';
    leadPlain = 'Tempo run';
  } else if (wt.includes('interval')) {
    leadWithComma = 'Intervals,';
    leadPlain = 'Intervals';
  } else if (wt.includes('hill')) {
    leadWithComma = 'Hill workout,';
    leadPlain = 'Hill workout';
  } else if (wt.includes('easy')) {
    leadWithComma = 'Easy run,';
    leadPlain = 'Easy run';
  } else if (wt.includes('recovery')) {
    leadWithComma = 'Recovery run,';
    leadPlain = 'Recovery run';
  } else {
    const wt0 = workoutType || 'Run';
    leadWithComma = `${wt0},`;
    leadPlain = wt0;
  }

  // When we have a useful em phrase (miles or rep×distance), render
  // "Hill workout, 5x400m." with the em italicized. When nothing
  // useful can be extracted, render just "Hill workout." — drop the
  // em entirely rather than fall back to "session", which read as
  // an awkward placeholder.
  if (em) {
    return { titleLead: leadWithComma, titleEm: em, titleTrail: '.' };
  }
  return { titleLead: leadPlain, titleEm: '', titleTrail: '.' };
}

function clip(text: string, max = SUBTITLE_MAX): string {
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1).trimEnd()}…`;
}

/**
 * Pick the most-useful subtitle text from the expanded breakdown,
 * falling back to the details column.
 */
function buildSubtitle(
  details: string | null | undefined,
  expanded: Record<string, string> | null,
): string {
  if (expanded) {
    const ordered = ['main_set', 'pace_target', 'coach_note', 'warmup'] as const;
    for (const key of ordered) {
      const v = expanded[key];
      if (typeof v === 'string' && v.trim().length > 0) return clip(v);
    }
  }
  if (details && details.trim().length > 0) return clip(details);
  return '';
}

/**
 * Build the optional hero coach-note from `expanded_details.coach_note`.
 * Skipped when the field is empty (e.g. plan generated before PR 5.0).
 */
function buildHeroCoachNote(
  expanded: Record<string, string> | null,
): TodaySession['heroCoachNote'] | undefined {
  if (!expanded) return undefined;
  const note = expanded.coach_note;
  if (typeof note !== 'string' || note.trim().length === 0) return undefined;
  return {
    coachName: HERO_COACH_NAME,
    timeAgo: 'just now',
    text: note,
  };
}

/**
 * Adapt a TrainingPlan row + the matching plan_day into a TodaySession.
 * Pure function — exported for unit tests.
 */
export function adaptToTodaySession(
  plan: TrainingPlanRow,
  planDay: PlanDayRow,
): TodaySessionWithFigures {
  const intensityLabel = intensityFromWorkoutType(planDay.workout_type);
  const phaseLabel = `Today · Week ${plan.week_number} · ${plan.intensity || 'Build'}`;
  const { titleLead, titleEm, titleTrail } = buildTitle(planDay.workout_type, planDay.details);
  const subtitle = buildSubtitle(planDay.details, planDay.expanded_details);
  // B338 fix 2 — only when the title phrase IS the day's distance and the text wrote it in both units.
  const hero = heroDistance(planDay.details);
  const titleEmFigures = hero && hero.phrase === titleEm ? hero.figures : null;

  // Stats: keep a max of 3 — the hero strip wraps awkwardly past that.
  const stats: TodaySession['stats'] = [];
  const milesPhrase = extractMileagePhrase(planDay.details);
  if (milesPhrase) {
    // B338 — a km phrase labels its stat km, never miles.
    const isKm = /\skm$/.test(milesPhrase);
    stats.push({
      value: milesPhrase.replace(isKm ? /\s*km$/ : /\s*miles$/i, ''),
      label: isKm ? 'km' : 'miles',
    });
  }
  const paceTarget = planDay.expanded_details?.pace_target;
  if (typeof paceTarget === 'string' && paceTarget.trim()) {
    // The pace_target text is often "8:30/mi" or "Conversational, 8:30/mi".
    // Try to surface a numeric "M:SS" snippet for the stat, full text in
    // the subtitle.
    const m = paceTarget.match(/(\d{1,2}:\d{2})/);
    if (m) stats.push({ value: m[1], label: 'pace /mi' });
  }
  if (intensityLabel && intensityLabel !== 'Rest') {
    stats.push({ value: intensityLabel, label: 'effort' });
  }

  return {
    phaseLabel,
    intensityLabel,
    titleLead,
    titleEm,
    titleTrail,
    subtitle,
    stats,
    heroCoachNote: buildHeroCoachNote(planDay.expanded_details),
    ...(titleEmFigures ? { titleEmFigures } : {}),
  };
}

export function useTodaySession({ seasonId, weekNumber }: UseTodaySessionArgs): UseTodaySessionResult {
  const { supabase, user } = useAuthContext();
  const userId = user?.id ?? null;

  const [data, setData] = useState<TodaySession | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  const refetch = useCallback(async () => {
    // Season-model 2b — NO MAX(week_number) fallback. Without a resolved
    // current week (weekNumber null → before_season/no_plan/invalid) there is
    // no "today's session" to show; return empty so the caller renders the
    // pre-season / empty-state instead of a stale week's workout.
    const hasWeek = typeof weekNumber === 'number' && weekNumber >= 1;
    if (!supabase || !userId || !seasonId || !hasWeek) {
      setData(null);
      setLoading(false);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: rows, error: queryError } = await supabase
        .from('training_plans')
        .select('id, week_number, title, intensity, plan_days(*)')
        .eq('user_id', userId)
        .eq('season_id', seasonId)
        .eq('week_number', weekNumber)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (queryError) throw queryError;
      const plan = (rows as TrainingPlanRow | null) ?? null;
      if (!plan || !plan.plan_days || plan.plan_days.length === 0) {
        setData(null);
        return;
      }

      // Find today's plan_day. Monday-based, local TZ.
      const todayMon = todayMondayBasedLocal();
      const planDay = plan.plan_days.find((pd) => pd.day_of_week === todayMon) ?? null;
      if (!planDay) {
        // Plan exists but no row for today's weekday — incomplete plan.
        setData(null);
        return;
      }

      setData(adaptToTodaySession(plan, planDay));
    } catch (err) {
      setData(null);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setLoading(false);
    }
  }, [supabase, userId, seasonId, weekNumber]);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  if (!supabase || !userId) {
    return { data: null, loading: false, error: null, refetch: async () => {} };
  }

  return { data, loading, error, refetch };
}

// Exported for unit-testing the pure helpers without a live client.
export const __testing__ = {
  adaptToTodaySession,
  intensityFromWorkoutType,
  buildTitle,
  buildSubtitle,
  buildHeroCoachNote,
  extractMileagePhrase,
  extractWorkoutEm,
  heroDistance,
};
